import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Logo } from "./Logo";
import { SITE } from "../content";

const DISMISS_KEY = "dmw_smart_banner_dismissed";

function detectPlatform() {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/.test(ua)) return "ios";
  if (/Android/.test(ua)) return "android";
  return null;
}

export function SmartAppBanner({ onGetApp }) {
  const [platform, setPlatform] = useState(null);
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    setPlatform(detectPlatform());
    try {
      setDismissed(localStorage.getItem(DISMISS_KEY) === "1");
    } catch {
      setDismissed(false);
    }
  }, []);

  const close = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {}
  };

  const open = () => {
    const url = platform === "ios" ? SITE.appStoreUrl : SITE.playStoreUrl;
    if (url) window.open(url, "_blank", "noopener,noreferrer");
    else onGetApp?.();
  };

  const show = platform && !dismissed;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="smart-app-banner"
          initial={{ y: 96, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 96, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-x-3 bottom-3 z-50 md:hidden"
        >
          <div className="flex items-center gap-3 rounded-2xl border border-white/[0.12] bg-panel/95 p-3 shadow-[0_20px_50px_-10px_rgba(0,0,0,0.9)] backdrop-blur-xl">
            <Logo size={42} />
            <div className="min-w-0 flex-1 leading-tight">
              <p className="truncate text-sm font-bold text-paper">DMWrapped</p>
              <p className="truncate text-xs text-dim">
                {platform === "ios" ? "Get it on the App Store" : "Get it on Google Play"}
              </p>
            </div>
            <button
              data-testid="smart-banner-get"
              onClick={open}
              className="rounded-full bg-acid px-5 py-2 text-sm font-bold text-ink transition-transform active:scale-95"
            >
              Get
            </button>
            <button
              data-testid="smart-banner-close"
              onClick={close}
              aria-label="Dismiss"
              className="p-1.5 text-dim transition-colors hover:text-paper"
            >
              <X size={18} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
